import {
  Paper,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableBody,
  TableRow,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useOktaAuth } from "@okta/okta-react";
import { ManagedAccountContext } from "../../context/ManagedAccountContext";

const WSU_RED = "#A60F2D";

const columns = [
  { id: "displayName",       label: "Display Name", minWidth: 150 },
  { id: "userPrincipalName", label: "UPN",          minWidth: 150 },
  { id: "mail",              label: "Email",         minWidth: 150 },
];

export const ManagedAccountsSearchResultTable = ({ text }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);

  const { authState } = useOktaAuth();
  const { managedAccountModule, setManagedAccountModule } = useContext(ManagedAccountContext);

  useEffect(() => {
    const fetchSearchResults = async () => {
      try {
        const res = await fetch(
            `${process.env.REACT_APP_AWS_API_GATEWAY_ENDPOINT}/managed-accounts/search`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                Authorization: authState.accessToken.accessToken,
              },
              body: JSON.stringify({ searchValue: text.trim() }),
            }
        );

        if (!res.ok) {
          if (res.status === 504) {
            throw new Error("The query took too long. Please refine your search value.");
          }
          const errorBody = await res.json().catch(() => ({}));
          throw new Error(errorBody?.error?.message || "An unknown error occurred.");
        }

        const result = await res.json();
        const formattedResults = (result.results || []).map((acc) => ({
          dn: acc.dn,
          displayName: acc.displayName,
          userPrincipalName: acc.userPrincipalName,
          mail: acc.mail,
        }));

        setManagedAccountModule((prev) => ({ ...prev, searchResults: formattedResults }));
        setIsLoaded(true);
      } catch (err) {
        setError(err);
        setIsLoaded(true);
      }
    };

    if (text?.trim()) {
      fetchSearchResults();
    } else {
      setManagedAccountModule((prev) => ({ ...prev, searchResults: [] }));
      setIsLoaded(true);
    }
  }, [authState.accessToken.accessToken, setManagedAccountModule, text]);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleRowClick = (row) => {
    setManagedAccountModule((prev) => ({
      ...prev,
      isOpen: true,
      mode: "read",
      upn: row.userPrincipalName,
      dn: row.dn,
    }));
  };

  const renderCell = (column, row) => {
    const value = row[column.id];
    return value ? (
        <Typography variant="body2">{value}</Typography>
    ) : (
        <Typography variant="body2" color="text.disabled" fontStyle="italic">—</Typography>
    );
  };

  const { searchResults = [] } = managedAccountModule;

  if (!isLoaded) {
    return (
        <Box display="flex" justifyContent="center" alignItems="center" py={6}>
          <CircularProgress style={{ color: WSU_RED }} size={80} thickness={5} />
        </Box>
    );
  }

  if (error) {
    return (
        <Paper elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
          <Box sx={{ backgroundColor: WSU_RED, color: "white", px: 3, py: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              Search failed
            </Typography>
            <Typography variant="body2">{error.message}</Typography>
          </Box>
        </Paper>
    );
  }

  if (!searchResults.length) {
    return (
        <Paper elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
          <Box sx={{ px: 3, py: 3 }}>
            <Typography variant="body2" color="text.disabled" fontStyle="italic">
              No managed accounts found for "{text}"
            </Typography>
          </Box>
        </Paper>
    );
  }

  return (
      <Paper elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: 440 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                    <TableCell
                        key={col.id}
                        align="center"
                        sx={{
                          minWidth: col.minWidth,
                          backgroundColor: "#4D4D4D",
                          color: "white",
                          fontWeight: 700,
                          fontSize: "0.8rem",
                          letterSpacing: 0.5,
                        }}
                    >
                      {col.label}
                    </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {searchResults
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                      <TableRow
                          key={row.userPrincipalName}
                          hover
                          onClick={() => handleRowClick(row)}
                          sx={{
                            cursor: "pointer",
                            "&:hover": { backgroundColor: "#fdf0f2" },
                            "&:last-child td": { borderBottom: 0 },
                          }}
                      >
                        {columns.map((col) => (
                            <TableCell key={`${row.userPrincipalName}-${col.id}`} align="center">
                              {renderCell(col, row)}
                            </TableCell>
                        ))}
                      </TableRow>
                  ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={searchResults.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: "1px solid #e0e0e0",
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                fontSize: "0.8rem",
              },
            }}
        />
      </Paper>
  );
};